import {
  DecisionAlreadyAnsweredDetails,
  ErrorEnvelope,
  NameConflictDetails,
  NameTakenDetails,
  ValidationFailedDetails,
  WebsiteRefusalDetails,
  websiteUnreadableSentence,
  type ConflictCode,
  type RecordedAnswer,
} from '@ni/shared';
import {
  CREDIT_PRICE_CHANGED_LINE,
  CREDITS_EXHAUSTED_LINE,
  fileTooBigLine,
  mergeKeptNameLine,
  ORG_SPEND_LIMIT_LINE,
  PLAN_EXCLUDES_PAID_WORK_LINE,
  RESUME_PRICE_CHANGED_LINE,
} from '../lib/copy';

/**
 * One decode path for the conflict-code registry (ui-design §6.5). Every code
 * has a behavior, not a generic toast; the two-user codes are information, never
 * errors.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;

  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Parses `{error: {code, message, details?}}`; anything else becomes a plain error. */
export async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  const parsed = ErrorEnvelope.safeParse(body);
  if (parsed.success) {
    const { code, message, details } = parsed.data.error;
    return new ApiError(response.status, code, message, details);
  }
  return new ApiError(
    response.status,
    `HTTP_${response.status}`,
    response.statusText || 'Request failed.',
  );
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function hasCode(error: unknown, code: ConflictCode): boolean {
  return isApiError(error) && error.code === code;
}

/**
 * `DECISION_ALREADY_ANSWERED` carries the standing answer. It may be the
 * server-only `{corrected: true}` — the row was latched by someone's correction.
 */
export function standingAnswerOf(error: unknown): RecordedAnswer | null {
  if (!isApiError(error) || error.code !== 'DECISION_ALREADY_ANSWERED') return null;
  const parsed = DecisionAlreadyAnsweredDetails.safeParse(error.details);
  return parsed.success ? parsed.data.standing_answer : null;
}

/**
 * `NAME_CONFLICT` carries the live namesakes the create refused to choose
 * between. Null for every other error, so New Person can ask this one question
 * of its mutation error and render the decision panel or the plain line.
 */
export function nameConflictOf(error: unknown): NameConflictDetails | null {
  if (!isApiError(error) || error.code !== 'NAME_CONFLICT') return null;
  const parsed = NameConflictDetails.safeParse(error.details);
  return parsed.success ? parsed.data : null;
}

/**
 * **A refusal the server wrote for a person**, or null.
 *
 * `VALIDATION_FAILED` is ordinarily a client bug — a body that does not match
 * the contract — and `errorLine` says so in one generic sentence, because the
 * Zod issue behind it ("Invalid input", a path array) is not something to put in
 * front of the office. But some of these issues are built by hand on the server
 * for exactly that purpose: "Only two organisations can be related to each
 * other." is a sentence a reader can act on, and it is thrown as a 400 because
 * that is what a body naming the wrong pair *is*.
 *
 * So the call site that knows it can earn one of those asks for it here and
 * falls back to `errorLine`. The first issue only: the server raises these one
 * at a time, and a list of them on an inbox card would be a form's error summary
 * where a sentence belongs.
 */
export function validationSentence(error: unknown): string | null {
  if (!isApiError(error) || error.code !== 'VALIDATION_FAILED') return null;
  const parsed = ValidationFailedDetails.safeParse(error.details);
  return parsed.success ? (parsed.data.issues[0]?.message ?? null) : null;
}

/**
 * **The name the rename could not take**, as the server wrote it, or null.
 *
 * `NAME_TAKEN` is not a client bug and not a generic conflict: it is one
 * sentence naming the name a live record already carries, and the reader's next
 * move — type a different one — is a thing they can do without leaving the modal
 * they are standing in. So the door that can earn it asks for the sentence here
 * and falls back to `errorLine`, exactly as {@link validationSentence} is asked
 * for by the doors that can earn one of those.
 *
 * The first issue only, for the same reason: the server raises these one at a
 * time, and a list of sentences where one belongs is a form's error summary.
 */
export function nameTakenSentence(error: unknown): string | null {
  if (!isApiError(error) || error.code !== 'NAME_TAKEN') return null;
  const parsed = NameTakenDetails.safeParse(error.details);
  return parsed.success ? (parsed.data.issues[0] ?? null) : null;
}

/**
 * **What was wrong with the website**, as the server wrote it, or null
 * (organisations-as-subjects note §2 chunk 2a).
 *
 * The refusal carries its sentence in the plain-string `issues` list
 * `NAME_TAKEN` uses, and for the same reason: the reader is standing on the
 * field, so there is no path to point at — only a sentence, and a next move they
 * can make without leaving the form. The New Organisation door prints it under
 * the website field; anywhere else falls back to {@link errorLine}.
 *
 * **There were two codes and there is one** (owner ruling, 2026-09-10).
 * `WEBSITE_ALREADY_CONFIRMED` refused a website because the record already had a
 * different one; a record may now hold more than one, so the second is recorded
 * beside the first and the only refusal left is the value that names no host.
 */
export function websiteRefusalOf(error: unknown): string | null {
  if (!isApiError(error)) return null;
  if (error.code !== 'WEBSITE_UNREADABLE') return null;
  const parsed = WebsiteRefusalDetails.safeParse(error.details);
  return parsed.success ? (parsed.data.issues[0] ?? null) : null;
}

/**
 * What a standing answer reads as in place. The envelope carries the answer body
 * and not who wrote it, so the line names the act and the recency, not a person.
 */
export function standingAnswerLine(answer: RecordedAnswer): string {
  if ('corrected' in answer) return 'Corrected a moment ago';
  if ('keep' in answer) return 'Kept a moment ago';
  if ('leave_out' in answer) return 'Left out a moment ago';
  if ('wrong_person' in answer) return 'Marked wrong person a moment ago';
  /* The one answer with a second half in it. "Same" on a likely-merge card also
     settles what the one record is called, and a reader who arrives to find the
     pair already answered wants that name — it is the part they cannot work out
     from the two names still on the card in front of them. */
  if ('kept_name' in answer) return mergeKeptNameLine(answer.kept_name);
  /* Every road to a bind reads the same, because they are the same act: the
     pick list's Confirm, its same-person answer, and the held list's "Select
     this person". */
  if (
    'chosen_candidate' in answer ||
    'chosen_candidates' in answer ||
    'candidate_ref' in answer ||
    'candidate_refs' in answer
  ) {
    return 'Identified a moment ago';
  }
  return 'Answered a moment ago';
}

/**
 * The line a surface shows when it cannot handle the code itself. Codes with a
 * behavior (refetch, render in place) are handled at their call site; this
 * covers the defensive and the unexpected.
 */
export function errorLine(error: unknown): string {
  if (!isApiError(error)) {
    return 'The change was not saved.';
  }
  switch (error.code) {
    /* Kind-neutral, because the code is: an organisation's lookup is refused by
       the same rule as a person's dossier (`state NOT IN ('complete','failed')`)
       and this line is shown on the record it was refused on. */
    case 'RUN_ALREADY_ACTIVE':
      return 'A run is already in progress for this record.';
    case 'STATE_CAS_FAILED':
      return 'The run state changed.';
    case 'IDENTITY_NOT_ESTABLISHED':
      return 'Identity is not established. Run a quick search first.';
    /* The organisation's half of the same rule (owner ruling, 2026-09-09), and
       the server's own sentence rather than a second spelling of it: the record
       page renders this under the button that earned it, and the button that
       clears it is the other one on the same strip. */
    case 'LOOKUP_FIRST':
      return 'Look this organization up before investigating it.';

    /* NAME_CONFLICT is a question, not a failure, and New Person renders it as
       the decision panel. The line below is the defensive one, for a surface
       that reaches the code without a panel to put it in. */
    case 'NAME_CONFLICT':
      return 'Someone with this name is already on record.';
    /* Reachable only in the race: the namesake became a subject between the
       conflict being shown and the record being picked. */
    case 'ENTITY_ALREADY_SUBJECT':
      return 'That person is already on record. Open their record from People.';

    /* The rename modal renders the server's own sentence, which names the name
       that is taken; this is the defensive line, for a surface that reaches the
       code without a field to send the reader back to. */
    case 'NAME_TAKEN':
      return 'Another record already goes by that name. Pick a different one.';

    /* ————— organisations (organisations-as-subjects note §2 chunk 2) ————— */

    /* Rendered in place by the door that can earn it — the New Organisation
       form, and Add what you know — under the website field it is about. This is
       the defensive line, for a surface that reaches the code with no field to
       point at.

       The server's own sentence is preferred where there is one; both come out of
       the same shared helper, so what the server sends and what this file falls
       back to cannot drift into two spellings of one refusal. */
    case 'WEBSITE_UNREADABLE':
      return websiteRefusalOf(error) ?? websiteUnreadableSentence();

    /* STATUS_NOT_YET_SUPPORTED had a line here and needs none: it is a
       server-side belt, and New Person disables the two statuses that would
       earn it, so no reader ever reaches the code. */

    /* ————— imports (import note §2.4) ————— */

    /* Nobody answered, and a moment later they might: this is the one import
       code whose line offers a retry, which is why it says "again". */
    case 'IMPORT_PLANNER_UNAVAILABLE':
      return 'Could not get a plan. Send again in a minute.';
    /* Somebody answered with a mapping the contract refuses. Sending the same
       words again gets the same answer, so the line asks for different ones. */
    case 'IMPORT_PLAN_INVALID':
      return 'The plan came back unusable. Try wording it differently.';
    case 'IMPORT_SESSION_TOO_LONG':
      return 'This conversation is too long. Start a new import for the rest.';
    /* The People tab renders this one in place as the stale-preview notice; the
       line below is the defensive one, for a door that reaches the code with
       nowhere to put it. */
    case 'IMPORT_NOT_PREVIEWED':
      return 'Run the preview again before importing.';
    case 'IMPORT_HAS_UNRESOLVED_ROWS':
      return 'Some rows still need review before this can be imported.';
    /* **The findings moved under the page** (§4.3). The Import tab already
       renders the list and holds the boxes, so reaching this code means the
       server recomputed and found something the page was not showing — which is
       exactly the case the recompute exists for. The poll brings the new list,
       so the line sends them back to the page rather than offering a button. */
    case 'IMPORT_HALTED':
      return 'The preview found something new about this file. Read the findings on this page and confirm them before importing.';
    /* **The one import stop that cannot be confirmed past** (§4.4). The office
       said a cell is wrong and the dry run could not read it that way, so
       importing would write the value they said was wrong. Unlike the findings
       above, what has to change is on the Plan tab — the correction, or the plan
       it sits on — so the line sends them there rather than to this page. */
    case 'IMPORT_CORRECTION_UNAPPLIED':
      return 'A correction on the plan was not applied, so this would import the old value. Fix or remove it on the Plan tab.';
    case 'IMPORT_ALREADY_COMMITTED':
      return 'This import has already run.';
    case 'IMPORT_NOT_COMMITTED':
      return 'This import never wrote anything, so there is nothing to undo.';
    case 'IMPORT_ALREADY_UNDONE':
      return 'This import has already been undone.';
    case 'IMPORT_STILL_IMPORTING':
      return 'This import is still writing. Wait for it to finish.';
    case 'IMPORT_REPLACE_STALE':
      return "The event's attendee list changed since the preview. Run the preview again.";
    case 'IMPORT_FILE_LOCKED':
      return 'The file cannot be replaced once the import has run.';
    case 'IMPORT_FILE_UNREADABLE':
      return 'That file could not be read. Try a CSV, a spreadsheet or a PDF.';
    case 'IMPORT_NOT_EDITABLE':
      return 'This import is finished, so it cannot be changed.';
    /* The two retry refusals (§4.2). Neither offers a button: the first says to
       use the session that is already open, and the second is a session nobody
       uploaded a file to, which nothing on this page can put right. */
    case 'IMPORT_STILL_EDITABLE':
      return 'This import is still open. Continue in it instead.';
    case 'IMPORT_HAS_NO_FILE':
      return 'This import has no file behind it, so there is nothing to start again on.';
    /* **The double press, and a sentence that cannot carry the link it wants.**
       `details.import_id` is the session that already exists and the honest line
       would be one that goes there — but every reader of this function renders
       its answer as a string (`ErrorLine` is `<div className="src">{...}</div>`),
       so there is nowhere to put an anchor without giving the whole error
       channel a shape it does not have. The words say where to go instead: the
       Imports ledger is one click away on the rail and the new session is its
       newest row. A linked version is a change to `ErrorLine`, not to this line. */
    case 'IMPORT_ALREADY_RETRIED':
      return 'This file has already been started again. The new import is the newest row on Imports.';
    /* **The one code the server never writes.** A file over the cap is refused
       by the upload interceptor before any handler sees it, so what comes back
       is a bare 413 with no envelope on it and `toApiError` synthesises this
       code from the status. Without a line, the only sentence in the product for
       "your 40 MB workbook is too big" was the browser's own statusText. The
       New import door checks the size before it sends, so this is the belt: a
       cap that moves, or a proxy with a smaller one in front. */
    case 'HTTP_413':
      return fileTooBigLine(null);

    /* ————— the organization's month (2026-09-09) ————— */

    /* **The same sentence the banner carries**, out of one constant. This code
       reaches a door the office pressed — Research, Investigate, an import turn
       — and Home is already saying it in a bar at the top of the page; two
       spellings of one refusal would read as two different problems.

       Nothing is read off `details`. It carries what was spent and what the
       limit was, and neither is the office's to see: the allowance is the
       operator's, and the line they need is in the log. */
    case 'ORG_MONTHLY_SPEND_CEILING_REACHED':
      return ORG_SPEND_LIMIT_LINE;

    /* ————— credits (credits launch spec, step 1) ————— */
    case 'CREDITS_EXHAUSTED':
      return CREDITS_EXHAUSTED_LINE;
    case 'CREDIT_PRICE_CHANGED':
      return CREDIT_PRICE_CHANGED_LINE;
    case 'PLAN_EXCLUDES_PAID_WORK':
      return PLAN_EXCLUDES_PAID_WORK_LINE;

    case 'VALIDATION_FAILED':
      return 'That was not accepted. Nothing was saved.';
    case 'OWNERSHIP_FENCE':
    case 'IDEMPOTENCY_KEY_REUSED':
      return 'Someone else changed this first.';
    default:
      return error.message || 'The change was not saved.';
  }
}

/** {@link errorLine} for a failed run's Resume, which shows no price to go and see. */
export function resumeErrorLine(error: unknown): string {
  return hasCode(error, 'CREDIT_PRICE_CHANGED') ? RESUME_PRICE_CHANGED_LINE : errorLine(error);
}

/**
 * **The line for a failed read.** `errorLine` is written for writes, and its
 * fallback for anything that is not an API error is "The change was not saved."
 * On a page that failed to load (a network drop, the server restarting) that
 * reads as if something the office did was lost, which it was not.
 */
export function loadErrorLine(error: unknown): string {
  if (!isApiError(error)) return LOAD_FAILED_LINE;
  const line = errorLine(error);
  return line === 'The change was not saved.' ? LOAD_FAILED_LINE : line;
}

export const LOAD_FAILED_LINE = 'This could not be loaded. Try again in a moment.';

/** A 404 on an org-scoped path may mean "not a member"; it never means "deleted". */
export function isNotFound(error: unknown): boolean {
  return isApiError(error) && error.status === 404;
}

/** The one retry `main.tsx` used to spell as `retry: 1`. */
const READ_RETRIES = 1;

/**
 * The QueryClient's retry policy (main.tsx): one retry, and only for a failure
 * worth asking about again — a fetch that never reached the server (a
 * `TypeError`, not an `ApiError`), a 429, or a 5xx. Any other 4xx is the
 * server's answer; retrying a 404 asks the same question again and gets the
 * same reply, one second later.
 *
 * The 4xx half matters more than it reads. TanStack's retryer pauses a pending
 * retry while `document.visibilityState` is `hidden` and resumes it only on
 * the next `visibilitychange`, so a read that 404s in a background tab never
 * reaches its error state until someone looks at it: the page sits on
 * "Loading." instead of "Not found." Not retrying the 4xx at all is what lets
 * a definitive answer land at once, visible or not. A transient failure's one
 * retry still waits for the tab to be seen — that is the retryer's rule for
 * not hammering a server nobody is watching, and it is left alone here.
 */
export function shouldRetryRead(failureCount: number, error: unknown): boolean {
  if (failureCount >= READ_RETRIES) return false;
  if (!isApiError(error)) return true;
  return error.status === 429 || error.status >= 500;
}
